/**
 * TASK-12181 — "Atendido por" na abertura de chamado (spec 2026-10-02-atendimento-por-organizacao §8).
 *
 * Substitui o TeamSelectCard: quem atende já vem resolvido pela categoria (ou pelo padrão da
 * plataforma) a partir de GET /v1/tenants/{tid}/chamados/atendimento, e "trocar" abre as
 * Organizações que podem atender o tenant, com as filas de cada uma.
 */
import { Anchor, Group, Loader, Select, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/api/client'

export interface FilaOpcao {
  fila_id: number
  nome: string
  alcance: 'interna' | 'externa'
  padrao: boolean
}

export interface OrgQueAtende {
  org_id: number
  org_nome: string
  filas: FilaOpcao[]
}

export interface AtendimentoAtual {
  org_id: number
  org_nome: string | null
  fila_id: number | null
  fila_nome: string | null
  origem: string
}

export interface AtendimentoPrevisto {
  atual: AtendimentoAtual | null
  opcoes: OrgQueAtende[]
}

/** O que o POST manda; nulo = deixar a cascata decidir (categoria, depois padrão). */
export interface AtendimentoEscolhido {
  attending_org_id: number
  queue_id: number
}

const MENSAGENS: Record<string, string> = {
  fila_de_outra_org: 'Essa fila não pertence à Organização escolhida.',
  fila_inativa: 'Essa fila está desativada. Escolha outra.',
  org_nao_atende: 'Essa Organização não tem fila ativa para receber chamados.',
  fila_interna_fora_da_org: 'Essa fila só atende os tenants da própria Organização.',
  sem_org_atendente_padrao: 'Não há Organização atendente configurada. Escolha uma em "trocar".',
}

/** Mensagem em português para o `detail` de um 422 de atendimento; nulo se não for um deles. */
export function mensagemDeAtendimento(detail: unknown): string | null {
  return typeof detail === 'string' ? MENSAGENS[detail] ?? null : null
}

export function rotuloDoAtendimento(org: string | null, fila: string | null): string {
  if (!org) return '—'
  return fila ? `${org} · fila ${fila}` : org
}

interface Props {
  tenantId: number | undefined
  categoryId: string | null
  value: AtendimentoEscolhido | null
  onChange: (v: AtendimentoEscolhido | null) => void
  /** Recebe o rótulo mostrado ("DEVOPS · fila DevOps"), para o resumo da abertura. */
  onRotulo?: (rotulo: string | null) => void
}

export function AtendidoPor({ tenantId, categoryId, value, onChange, onRotulo }: Props) {
  const [trocando, setTrocando] = useState(false)
  const { data, isLoading, isError } = useQuery<AtendimentoPrevisto>({
    queryKey: ['chamados-atendimento', tenantId, categoryId],
    enabled: !!tenantId,
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/chamados/atendimento`, {
        params: categoryId ? { category_id: Number(categoryId) } : {},
      })
      return res.data
    },
  })

  const opcoes = useMemo(
    () => (data?.opcoes ?? []).map((o) => ({
      group: o.org_nome,
      items: o.filas.map((f) => ({
        value: `${o.org_id}:${f.fila_id}`,
        label: f.padrao ? `${f.nome} (padrão)` : f.nome,
      })),
    })),
    [data],
  )

  const escolhida = useMemo(() => {
    if (!value || !data) return null
    const org = data.opcoes.find((o) => o.org_id === value.attending_org_id)
    const fila = org?.filas.find((f) => f.fila_id === value.queue_id)
    return org && fila ? { org: org.org_nome, fila: fila.nome } : null
  }, [value, data])

  const atual = data?.atual ?? null
  const rotulo = escolhida
    ? rotuloDoAtendimento(escolhida.org, escolhida.fila)
    : atual ? rotuloDoAtendimento(atual.org_nome, atual.fila_nome) : null
  useEffect(() => { onRotulo?.(rotulo) }, [rotulo, onRotulo])

  if (isLoading) return <Loader size="xs" />
  if (isError) {
    return <Text size="sm" c="red.7">Não foi possível saber quem atende. Tente recarregar.</Text>
  }

  return (
    <Stack gap={6}>
      <Group gap="xs" wrap="wrap" align="baseline">
        <Text size="sm">
          {rotulo ? <>Atendido por: <Text span fw={600}>{rotulo}</Text></> : 'Nenhuma Organização atende este tenant por padrão.'}
        </Text>
        {opcoes.length > 0 && (
          <Anchor component="button" type="button" size="sm" onClick={() => setTrocando((t) => !t)}>
            {trocando ? 'fechar' : 'trocar'}
          </Anchor>
        )}
        {value && (
          <Anchor component="button" type="button" size="sm" c="dimmed"
                  onClick={() => { onChange(null); setTrocando(false) }}>
            voltar ao padrão
          </Anchor>
        )}
      </Group>
      {trocando && (
        <Select
          aria-label="Organização e fila que atendem"
          placeholder="Escolha a Organização e a fila"
          data={opcoes}
          searchable
          value={value ? `${value.attending_org_id}:${value.queue_id}` : null}
          onChange={(v) => {
            if (!v) { onChange(null); return }
            const [org, fila] = v.split(':').map(Number)
            onChange({ attending_org_id: org, queue_id: fila })
            setTrocando(false)
          }}
        />
      )}
    </Stack>
  )
}
